import React, { useRef, useState, useEffect } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm        from '../../components/RSVPForm';
import Wishlist        from '../../components/Wishlist';
import YandexMap       from '../../components/YandexMap';
import Countdown       from '../../components/Countdown';
import FixedCTA        from '../../components/FixedCTA';
import useReveal       from '../../components/useReveal';
import Schedule        from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import CalendarButton  from '../../components/CalendarButton';
import BlockSubtitle   from '../../components/BlockSubtitle';
import PaidWatermark   from '../../components/PaidWatermark';
import MusicPlayer from '../../components/MusicPlayer';
import './OnaSkazalaDa.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : u;
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

/* "DD/MM/YYYY" to match the mockup's slash-separated dates. */
const fmtSlash = raw => {
  if (!raw) return '12/12/2026';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '12/12/2026';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

/* Default greyscale dresscode palette from the Figma mockup. */
const DEFAULT_SWATCHES = ['#000000', '#373737', '#5c5c5c', '#808080', '#c6c6c6', '#efefef'];

/* Self-host the three families the mockup needs:
   - Comforter     → the giant calligraphic «да» (Cyrillic-capable; the
     mockup's Corinthia has no Cyrillic glyphs).
   - Montserrat    → headings / body (variable weight).
   - Inter         → the thin Helvetica-Neue-style nav / kicker / hero date.
   Injected as a runtime <style> (like Rustic) so the chatbot's cloned-style
   iframe preview inherits the @font-face rules. */
function useOsdFonts() {
  useEffect(() => {
    if (document.getElementById('osd-fonts')) return;
    const base = '/templates/ona-skazala-da/fonts';
    const style = document.createElement('style');
    style.id = 'osd-fonts';
    style.textContent =
      `@font-face{font-family:'Comforter';`
        + `src:url('${base}/Comforter-Regular.ttf') format('truetype');`
        + `font-weight:400;font-style:normal;font-display:swap;}`
      + `@font-face{font-family:'OSD Montserrat';`
        + `src:url('${base}/Montserrat-var.ttf') format('truetype');`
        + `font-weight:300 700;font-style:normal;font-display:swap;}`
      + `@font-face{font-family:'OSD Inter';`
        + `src:url('${base}/Inter-var.ttf') format('truetype');`
        + `font-weight:100 500;font-style:normal;font-display:swap;}`;
    document.head.appendChild(style);
  }, []);
}

/* ── Class maps for shared components ─────────────────────── */
const CD_CLASSES    = { root: 'osd-cd',    cell: 'osd-cd-cell', num: 'osd-cd-num', label: 'osd-cd-lbl' };
const SCHED_CLASSES = { root: 'osd-sched', row: 'osd-sched-row', rowBorder: 'osd-sched-row--border', time: 'osd-sched-time', info: 'osd-sched-info', name: 'osd-sched-name', desc: 'osd-sched-desc' };
const DRESS_CLASSES = { wrap: 'osd-swatches', swatchWrap: 'osd-swatch-wrap', swatch: 'osd-swatch', swatchLabel: 'osd-swatch-lbl', photos: 'osd-dc-photos', photo: 'osd-dc-photo' };
const RSVP_CLASSES = {
  form:    'osd-form',
  tabs:    'osd-rsvp-tabs',
  tab:     'osd-rsvp-tab',
  tabSel:  'osd-rsvp-tab--sel',
  field:   'osd-form-field',
  label:   'osd-form-lbl',
  input:   'osd-form-input',
  submit:  'osd-btn osd-btn--primary osd-btn--full',
  success: 'osd-form-ok',
  error:   'osd-form-err',
  chip:    'osd-chip',
  chipSel: 'osd-chip--sel',
  chips:   'osd-form-chips',
};
const WISHLIST_CLASSES = {
  list:        'osd-wish',
  item:        'osd-wish-item',
  itemBorder:  'osd-wish-item--border',
  row:         'osd-wish-row',
  name:        'osd-wish-name',
  price:       'osd-wish-price',
  btn:         'osd-btn osd-btn--primary osd-btn--full',
  btnReserved: 'osd-btn--res',
};
const WISHLIST_MODAL_CLASSES = {
  bg:         'osd-wish-modal-bg',
  modal:      'osd-wish-modal',
  title:      'osd-wish-modal-title',
  subtitle:   'osd-wish-modal-sub',
  input:      'osd-form-input',
  error:      'osd-form-err',
  btns:       'osd-wish-modal-btns',
  btnCancel:  'osd-btn osd-btn--outline',
  btnConfirm: 'osd-btn osd-btn--primary',
};

/* Inline-SVG colour swatch — an actual <svg> element whose fill follows the
   user-selected dresscode colour (the requirement: SVG dresscode elements
   recolour to the user's chosen palette). */
function SvgSwatch({ color }) {
  const isLight = typeof color === 'string'
    && ['#fff', '#ffffff', '#efefef', '#f3f4f6'].includes(color.toLowerCase());
  return (
    <div className="osd-swatch-wrap">
      <svg className="osd-swatch" viewBox="0 0 58 58" preserveAspectRatio="none"
           role="img" aria-hidden="true">
        <rect x="0" y="0" width="58" height="58" fill={color} />
        <rect x="0.5" y="0.5" width="57" height="57" fill="none"
              stroke={isLight ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.4)'} strokeWidth="1" />
      </svg>
    </div>
  );
}

/* ══ Main Template ═════════════════════════════════════════════ */
export default function OnaSkazalaDa({ project, design, isPreview }) {
  const pageRef  = useRef(null);
  /* В зависимость включаем и наличие фото: секции с фото появляются
     динамически (после загрузки в превью) — иначе reveal-обсёрвер их не
     подхватывает и они остаются скрытыми (opacity: 0). */
  const blockDep = `${project?.blocks?.length}|${project?.cover_photos?.length || 0}|${project?.footer_photo?.url ? 1 : 0}|${project?.dresscode_photos?.length || 0}`;
  useReveal(pageRef, blockDep, { revealSelector: '.osd-reveal', inClass: 'osd-in' });
  useOsdFonts();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};

  /* Colours — monochrome by default (black on white); palette can override.
     The look is strictly two-tone, so text + accents share one colour. */
  const accent = d.palette?.primary   || p.palette?.primary   || '#000000';
  const bg     = d.palette?.secondary || p.palette?.secondary || '#FFFFFF';
  const fg     = accent;

  /* Blocks */
  const blocks = p.blocks || ['menu','map','schedule','palette','rsvp','survey','message','wishlist','countdown','calendar'];
  const HAS_MENU     = blocks.includes('menu');
  const HAS_CALENDAR = blocks.includes('calendar');

  /* Text */
  const bride       = p.bride || 'Невеста';
  const groom       = p.groom || 'Жених';
  const footerQuote = p.block_content?.footer?.quote || 'Спасибо, что вы с нами';
  const invText     = p.invitation_text || p.block_content?.message?.text
    || 'С огромным волнением и радостью хотим пригласить вас на ужин в честь нашего бракосочетания!\n\nВ этот особенный день мы будем счастливы видеть каждого из вас, чтобы разделить волшебство момента и создать воспоминания, которые останутся в наших сердцах навсегда.\n\nПожалуйста, подтвердите своё присутствие через форму на нашем сайте или через любой мессенджер.';
  const invTitle    = p.block_content?.message?.title || 'Дорогие гости';

  /* Photos */
  const photos        = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const heroPhoto     = src(photos[0]?.url) || null;
  const heroPhotoMeta = photos[0] || null;
  const dcPhotos      = p.dresscode_photos || [];
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  /* Date */
  const dateSlash = fmtSlash(p.wedding_date);

  /* Schedule */
  const schedule = p.schedule?.length ? p.schedule : [];

  /* Venues */
  const venues = p.venues || [];

  /* Dresscode — fall back to the mockup's greyscale set ONLY when the field
     was never set (undefined/null). An explicit empty array means the user
     deleted every colour, so we honour it and render no swatches instead of
     substituting the template defaults. */
  const rawSwatches = Array.isArray(p.dresscode_palette) ? p.dresscode_palette : DEFAULT_SWATCHES;
  const swatchColors = rawSwatches
    .map(sw => (sw && typeof sw === 'object') ? (sw.color || '') : sw)
    .filter(Boolean);

  /* Wishlist */
  const wishlist = p.wishlist || [];

  /* Block content titles */
  const bc = p.block_content || {};
  const titleSchedule = bc.schedule?.title  || 'Программа';
  const titleLocation = bc.map?.title       || 'Локация';
  const titleDress    = bc.palette?.title   || 'Дресс-код';
  const titleRsvp     = bc.rsvp?.title      || 'Подтвердите присутствие';
  const titleWish     = bc.wishlist?.title  || 'Что дарить';
  const titleCD       = bc.countdown?.title || 'До нашей свадьбы';
  const calBtn        = bc.calendar?.title  || 'Добавить в календарь';

  return (
    <div className={`osd-page${inPreview ? ' osd-page--preview' : ''}`} ref={pageRef}
      style={{ '--osd-accent': accent, '--osd-bg': bg, '--osd-fg': fg }}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {/* Navigation */}
      {HAS_MENU && (
        <nav className="osd-nav osd-reveal">
          {blocks.includes('schedule') && <a href="#osd-schedule">{bc.menu?.schedule || 'Программа'}</a>}
          {blocks.includes('map')      && <a href="#osd-location">{bc.menu?.location || 'Локация'}</a>}
          {blocks.includes('palette')  && <a href="#osd-dresscode">{bc.menu?.dresscode || 'Дресс-код'}</a>}
          {blocks.includes('rsvp')     && <a href="#osd-rsvp">{bc.menu?.rsvp || 'Анкета'}</a>}
        </nav>
      )}

      {/* Hero — kicker + giant calligraphic «да» + names + date */}
      <section className="osd-hero osd-reveal">
        <p className="osd-hero-kicker">Она сказала</p>
        <p className="osd-hero-da">да</p>
        <div className="osd-hero-meta">
          <div className="osd-hero-names">
            <span className="osd-hero-name">{bride}</span>
            <span className="osd-hero-and">{bc.header?.amp || 'и'}</span>
            <span className="osd-hero-name">{groom}</span>
          </div>
          <p className="osd-hero-date">{dateSlash}</p>
        </div>
      </section>

      {/* Invitation letter */}
      <section className="osd-invite osd-reveal">
        <h2 className="osd-invite-title">{invTitle}</h2>
        <div className="osd-invite-body">
          {invText.split('\n').map((line, i) =>
            line.trim() ? <p key={i}>{line}</p> : <br key={i} />
          )}
        </div>
      </section>

      {/* Cover photo — optional; only shown once a photo is uploaded.
         Sits in the block directly under the invitation text. */}
      {heroPhoto && (
        <section className="osd-cover osd-reveal">
          <div className="osd-hero-photo">
            <img src={heroPhoto} alt="" style={{ filter: flt(heroPhotoMeta), objectPosition: objPos(heroPhotoMeta) }} />
          </div>
        </section>
      )}

      {/* Date + calendar button */}
      <section className="osd-cal osd-reveal">
        <p className="osd-cal-date">{dateSlash}</p>
        {HAS_CALENDAR && (
          <CalendarButton
            bride={bride}
            groom={groom}
            date={p.wedding_date}
            time={p.wedding_time}
            venues={p.venues}
            address={p.wedding_address}
            className="osd-btn osd-btn--outline osd-btn--full">
            {calBtn}
          </CalendarButton>
        )}
      </section>

      {/* Dynamic blocks — Figma order */}
      {['map','schedule','palette','rsvp','wishlist','countdown']
        .filter(b => blocks.includes(b))
        .map(blockId => {
          switch (blockId) {
            case 'map':
              return (
                <section key="map" className="osd-map-sect osd-reveal" id="osd-location">
                  <div className="osd-map-head">
                    <h2 className="osd-h">{titleLocation}</h2>
                    <BlockSubtitle text={bc.map?.subtitle} className="osd-addr" />
                    {venues.map((v, i) => (
                      <p key={i} className="osd-addr">{v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}</p>
                    ))}
                    {!venues.some(v => v.address) && p.wedding_address && (
                      <p className="osd-addr">{p.wedding_address}</p>
                    )}
                  </div>
                  {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
                  {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
                  <div className="osd-map-wrap">
                    {(() => {
                      if (p.map_url) {
                        return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                      }
                      const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                      if (withCoords.length > 0) {
                        return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#blackStretchyIcon" />;
                      }
                      const addr = venues.length > 0
                        ? venues.map(v => v.address).filter(Boolean).join(' — ')
                        : (p.wedding_address || '');
                      if (!addr) return null;
                      const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                      return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                    })()}
                  </div>
                  )}
                </section>
              );

            case 'schedule':
              return (
                <section key="schedule" className="osd-sched-sect osd-reveal" id="osd-schedule">
                  <h2 className="osd-h">{titleSchedule}</h2>
                  <BlockSubtitle text={bc.schedule?.subtitle} className="osd-p" />
                  <Schedule items={schedule} classes={SCHED_CLASSES} />
                </section>
              );

            case 'palette':
              return (
                <section key="palette" className="osd-dress-sect osd-reveal" id="osd-dresscode">
                  <h2 className="osd-h">{titleDress}</h2>
                  <BlockSubtitle
                    text={bc.palette?.subtitle
                      || 'Нам будет очень приятно, если вы поддержите атмосферу праздника и выберете образы в предложенной цветовой гамме'}
                    className="osd-p" asHtml={false} />
                  <div className="osd-swatches">
                    {swatchColors.map((c, i) => <SvgSwatch key={i} color={c} />)}
                  </div>
                  <DresscodePalette
                    swatches={[]}
                    photos={dcPhotos}
                    cssFilter={flt}
                    classes={DRESS_CLASSES}
                  />
                </section>
              );

            case 'rsvp':
              return (
                <section key="rsvp" className="osd-rsvp-sect osd-reveal" id="osd-rsvp">
                  <h2 className="osd-h">{titleRsvp}</h2>
                  <BlockSubtitle text={bc.rsvp?.subtitle} className="osd-p" />
                  <RSVPForm project={p} blocks={blocks} classes={RSVP_CLASSES} />
                </section>
              );

            case 'wishlist':
              return (
                <section key="wishlist" className="osd-wish-sect osd-reveal">
                  <h2 className="osd-h">{titleWish}</h2>
                  <BlockSubtitle
                    text={bc.wishlist?.subtitle
                      || 'Ваше присутствие — самый ценный подарок для нас. Если желаете подарить что-то особенное — мы составили виш-лист, который поможет упростить поиск.'}
                    className="osd-p" asHtml={false} />
                  <Wishlist
                    items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                    slug={p.slug}
                    classes={WISHLIST_CLASSES}
                    modalClasses={WISHLIST_MODAL_CLASSES}
                  />
                </section>
              );

            case 'countdown':
              return (
                <section key="countdown" className="osd-cd-sect osd-reveal">
                  <h2 className="osd-h">{titleCD}</h2>
                  <BlockSubtitle text={bc.countdown?.subtitle} className="osd-p" />
                  <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={CD_CLASSES} />
                </section>
              );

            default:
              return null;
          }
        })}

      {/* Footer — photo + thank-you */}
      <footer className="osd-footer osd-reveal">
        {footerPhoto && (
          <div className="osd-footer-photo">
            <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        )}
        <div className="osd-footer-quote">
          <p>{footerQuote.split('\n').map((line, i, arr) => (
            <React.Fragment key={i}>
              {line}{i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}</p>
        </div>
      </footer>

      {/* Floating RSVP CTA */}
      {blocks.includes('rsvp') && (
        <FixedCTA
          href="#osd-rsvp"
          targetId="osd-rsvp"
          scrollRoot={pageRef}
          className="osd-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
